import { useEffect, useMemo, useState } from "react";
import { useTranslations } from "../i18n/LanguageProvider";
import { Header } from "../components/ui/header";
import { money } from "../utilities/currency";
import axios from "axios";
import { Footer } from "../components/ui/footer";
import { CheckCircle, Smartphone, Calendar, CreditCard, ShieldCheck, BarChart3 } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "../components/ui/button";
import ContactSalesModal from "./ContactSalesModal.tsx";
import { Link } from "react-router-dom";
import SalesBookingModal from "./SalesBookingModal.tsx";


type VideoServiceProps = {
    currentUser: any,
    onLogout: any,
    baseUrl: string,
    onLoginClick: any,
    onLoginSuccess: any,
    client: any,
}

type KAM = {
    id: string;
    name: string;
    country: string; // ISO-2
    available: boolean;
    recommended?: boolean;
    phone?: string; // E.164, e.g. +47...
    headshotSrc?: string;
};

function routeKAMsByCountry(kams: KAM[], userCountry: string): KAM[] {
    // Simple geo routing: exact country match first, else return all.
    const exact = kams.filter((k) => k.country === userCountry);
    return exact.length ? exact : kams;
}

function sortKAMs(kams: KAM[]): KAM[] {
    // Recommended first, then available, then name.
    return [...kams].sort((a, b) => {
        const ar = a.recommended ? 1 : 0;
        const br = b.recommended ? 1 : 0;
        if (ar !== br) return br - ar;

        const aa = a.available ? 1 : 0;
        const ba = b.available ? 1 : 0;
        if (aa !== ba) return ba - aa;

        return a.name.localeCompare(b.name);
    });
}

export default function VideoServicePage({ currentUser, onLogout, baseUrl, onLoginClick, onLoginSuccess, client }: VideoServiceProps) {
    const [wallet, setWallet] = useState(0);
    const [scrolled, setScrolled] = useState(false);

  const T = useTranslations("home");

    useEffect(() => {
        if (currentUser?.role === "customer") {
            axios.get(baseUrl + "/getCustomerWallet", { params: { token: currentUser.token } })

                .then((response) => {
                    if (response.data.status === "OK") {
                        setWallet(response.data.data.remaining_amount);
                    }
                });
        }
    }, [currentUser, baseUrl])

    const theme_settings = client ? JSON.parse(client.theme_settings) : {};

    const primaryColor = theme_settings.primaryColor || "#7c3aed"; // default purple-700
    const secondaryColor = theme_settings.secondaryColor || "#ec4899";

    const [appearance, setAppearance] = useState({
        gradientEnabled: false,
        gradientFrom: `${primaryColor}`,
        gradientTo: `${secondaryColor}`,
    });

    useEffect(() => {
        setAppearance(prev => ({
            gradientFrom: theme_settings.primaryColor,
            gradientTo: theme_settings.secondaryColor,
            gradientEnabled: theme_settings.useGradient === "true",
        }));
    }, [theme_settings.useGradient]);

    const closeAllModals = () => {
        setOpenDemo(false);
        setOpenSales(false);
        setShowKAM(false);
    };
    const [openDemo, setOpenDemo] = useState(false);
    const [openSales, setOpenSales] = useState(false);
    const [showKAM, setShowKAM] = useState(false);

    // Close modals on ESC (priority: KAM -> Success -> Demo -> Sales)
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return;
            if (showKAM) return void setShowKAM(false);
            if (openDemo) return void setOpenDemo(false);
            if (openSales) return void setOpenSales(false);
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [showKAM, openDemo, openSales]);

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                closeAllModals();
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, []);

    // Mock – replace with geo/IP later
    const userCountry = "NO";

    // Mock data – replace with API later
    const kams: KAM[] = useMemo(
        () => [
            {
                id: "kam_anna",
                name: "Anna Berg",
                country: "NO",
                available: true,
                recommended: true,
                phone: "+4700000000",
                headshotSrc: "/stock/headshot-anna.jpg",
            },
            {
                id: "kam_thomas",
                name: "Thomas Johansen",
                country: "NO",
                available: false,
                phone: "+4700000001",
                headshotSrc: "/stock/headshot-thomas.jpg",
            },
            {
                id: "kam_lars",
                name: "Lars Nilsen",
                country: "SE",
                available: true,
                phone: "+4600000000",
                headshotSrc: "/stock/headshot-lars.jpg",
            },
        ],
        []
    );

    const routedKams = useMemo(
        () => sortKAMs(routeKAMsByCountry(kams, userCountry)),
        [kams, userCountry]
    );

    return (
        <div style={{
            "--primary-color": primaryColor,
            "--secondary-color": secondaryColor,
        }} className={"bg-white text-gray-900"}>
            <Header wallet={wallet} scrolled={scrolled} T={T} onLoginClick={onLoginClick} currentUser={currentUser} onLogout={onLogout} baseUrl={baseUrl} />
            <main className="min-h-screen bg-gray-50 text-gray-800 py-16 px-6 md:px-12 lg:px-24">
                <div className="max-w-7xl mx-auto px-6">
                    <Link
                        to="/"
                        className="mb-6 text-[var(--primary-color)] hover:text-blue-800 text-sm font-medium"
                    >
                        ← Tilbake
                    </Link>
                    {/* Header */}
                    <div className="mb-16 max-w-3xl">
                        <h2 className="text-4xl font-semibold tracking-tight text-slate-900">
                            Videoløsninger med ekte mennesker
                        </h2>
                        <p className="mt-4 text-lg text-orange-600">
                            Book, betal og møt mennesker på video – én sømløs opplevelse med Vipps, kortbetaling og faktura.
                        </p>
                    </div>

                    {/* Visual grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {/* 1:1 Booking */}
                        <motion.div
                            whileHover={{ y: -6 }}
                            transition={{ duration: 0.3 }}
                            className="rounded-2xl bg-white border border-slate-200 overflow-hidden"
                        >
                            <div className="relative h-48">
                                <img
                                    src="https://images.unsplash.com/photo-1588196749597-9ff075ee6b5b?auto=format&fit=crop&w=1200&q=80"
                                    alt="1:1 video call"
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                            </div>
                            <div className="p-6">
                                <div className="text-xs font-medium text-orange-600 mb-2">1:1 VIDEO</div>
                                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                                    Booking med Vipps
                                </h3>
                                <p className="text-sm text-slate-600">
                                    Kunden velger tidspunkt, bekrefter med Vipps eller bankID og møtes på video.
                                    Betaling forhåndsautoriseres og holdes til møtet er fullført.
                                </p>
                            </div>
                        </motion.div>

                        {/* Live */}
                        <motion.div
                            whileHover={{ y: -6 }}
                            transition={{ duration: 0.3 }}
                            className="rounded-2xl bg-white border border-slate-200 overflow-hidden"
                        >
                            <div className="relative h-48">
                                <img
                                    src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1200&q=80"
                                    alt="Live group video"
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                                <div className="absolute top-3 left-3 rounded-full bg-orange-500 text-white text-xs px-3 py-1">
                                    LIVE
                                </div>
                            </div>
                            <div className="p-6">
                                <div className="text-xs font-medium text-orange-600 mb-2">LIVE</div>
                                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                                    Grupper & broadcast
                                </h3>
                                <p className="text-sm text-slate-600">
                                    Stream live til påmeldte deltakere. Start planlagt eller instant,
                                    med eller uten interaksjon og betalingsmur.
                                </p>
                            </div>
                        </motion.div>

                        {/* Course */}
                        <motion.div
                            whileHover={{ y: -6 }}
                            transition={{ duration: 0.3 }}
                            className="rounded-2xl bg-white border border-slate-200 overflow-hidden"
                        >
                            <div className="relative h-48">
                                <img
                                    src="https://images.unsplash.com/photo-1513258496099-48168024aec0?auto=format&fit=crop&w=1200&q=80"
                                    alt="Online course"
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                            </div>
                            <div className="p-6">
                                <div className="text-xs font-medium text-orange-600 mb-2">KURS</div>
                                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                                    Kursagent & moduler
                                </h3>
                                <p className="text-sm text-slate-600">
                                    Publiser kursvideoer i moduler eller på dato. Sett dato, pris og publiser. Resten går av seg selv!
                                </p>
                            </div>
                        </motion.div>
                    </div>

                    {/* CTA */}
                    <div className="mt-16 flex flex-col sm:flex-row gap-4">
                        <button
                            onClick={() => setOpenDemo(true)}
                            className="flex items-center rounded-2xl text-white px-5 py-2 shadow transition hover:brightness-90"
                            style={{ backgroundColor: "var(--primary-color)" }}
                        >
                            Book demo
                        </button>
                        <button
                            onClick={() => setOpenSales(true)}
                            className="flex items-center rounded-2xl text-dark px-5 py-2 shadow transition hover:brightness-90"
                            style={{ backgroundColor: "var(--secondary-color)" }}
                        >
                            Kontakt salg
                        </button>
                    </div>
                </div>

                {/* Demo booking modal */}
                <SalesBookingModal
                    open={openDemo}
                    onClose={() => setOpenDemo(false)}
                    baseUrl={baseUrl}
                    client={client}
                />

                {/* Contact sales modal */}
                <ContactSalesModal
                    open={openSales}
                    onClose={() => setOpenSales(false)}
                    onCallClick={() => {
                        setOpenSales(false);
                        setShowKAM(true);
                    }}
                    showKAM={showKAM}
                    setShowKAM={setShowKAM}
                    setOpenSales={setOpenSales}
                    client={client}
                    baseUrl={baseUrl}
                />

            </main>
            <Footer baseUrl={baseUrl} />
        </div>
    );
}
